import {
    addHours,
    addWeeks,
    compareAsc,
    format,
    isWithinInterval
} from 'date-fns';

import { WEEK_DAYS_COUNT } from '@nw/shared/config';

import {
    degreeBranchOptions,
    engineeringBranchOptions
} from '../../constants/BranchConstants';
import { STRING_DELIMITER } from '../../constants/MiscellaneousConstants';
import { WEEK_WISE } from '../../constants/SlotConstants';
import {
    formatDatesStringToNewDateFormat,
    getNextDatesForDayCodes
} from '../../utils/DateTimeUtils';

export const getSortedSlotOptions = (slotOptions: any[]) => {
    const sortedSlotOptions = slotOptions.sort((maxTime1, maxTime2) => {
        const maxTime1Array = maxTime1.max_visibility_time.split(':');
        const maxTime2Array = maxTime2.max_visibility_time.split(':');
        const maxTime1Value =
            parseInt(maxTime1Array[0]) * 60 + parseInt(maxTime1Array[1]);
        const maxTime2Value =
            parseInt(maxTime2Array[0]) * 60 + parseInt(maxTime2Array[1]);
        return maxTime1Value - maxTime2Value;
    });
    return sortedSlotOptions;
};

export const getFilterOptionsByDependentValue = (
    slotOptions,
    dependentFieldValue: string
) => {
    const filteredOptions = slotOptions.filter((eachOption) =>
        eachOption.match_keys.includes(dependentFieldValue)
    );
    return filteredOptions;
};

export const getSlotsTimeBasedOnDayCode = (
    slotOptions,
    dayCode: number,
    optionType: string,
    date: Date
) => {
    return slotOptions.filter((option) => {
        const { slot_hold_dates: slotHoldDates } = option;
        if (slotHoldDates) {
            const slotHoldDatesArray =
                formatDatesStringToNewDateFormat(slotHoldDates);
            date.setHours(0, 0, 0, 0);
            return (
                option.preferred_days.includes(dayCode) &&
                option.option_type === optionType &&
                !slotHoldDatesArray.includes(String(date))
            );
        } else
            return (
                option.preferred_days.includes(dayCode) &&
                option.option_type === optionType
            );
    });
};

export const getSlotTimesBasedOnDate = (
    slotOptions,
    optionType: string,
    date: Date
) => {
    return slotOptions.filter((option) => {
        const { extra_dates_to_show_slot: extraDatesToShowSlots } = option;
        if (!extraDatesToShowSlots) return false;
        const extraDatesToShowSlotsArray = formatDatesStringToNewDateFormat(
            extraDatesToShowSlots
        );
        return (
            option.option_type === optionType &&
            extraDatesToShowSlotsArray.includes(String(date))
        );
    });
};

export const removeSlotBasesOnConditions = (
    slotConditions,
    slotOptions,
    values //NOTE:values is used for eval condition
) => {
    // NOTE: below function are used for eval condition
    const isEngineeringBackground = (branchValue: string) => {
        return engineeringBranchOptions.includes(branchValue);
    };

    const isDegreeBackground = (branchValue: string) => {
        return degreeBranchOptions.includes(branchValue);
    };

    const conditionsKey = Object.keys(slotConditions);
    conditionsKey.forEach((key) => {
        const condition = slotConditions[key];
        slotConditions[key] = eval(condition);
    });
    const filteredOptions = slotOptions.filter((eachOption) => {
        const { condition } = eachOption;
        if (condition) {
            return eval(condition);
        }
        return true;
    });
    return filteredOptions;
};

export const getAllWeekDayToShowSlots = (slotOptions): number[] => {
    let webinarDays = [];
    slotOptions.forEach((option) => {
        if (option.option_type === WEEK_WISE) {
            webinarDays = [...webinarDays, ...option.preferred_days];
        }
    });
    const uniqueWebinarDays = Array.from(new Set(webinarDays));
    uniqueWebinarDays.sort();
    if (uniqueWebinarDays[0] === WEEK_DAYS_COUNT.SUNDAY) {
        uniqueWebinarDays.push(uniqueWebinarDays.shift());
    }
    return uniqueWebinarDays;
};

export const getAllPreferredDatesToShowSlots = (
    slotHoldDates: string[]
): Date[] => {
    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);
    const nextDayDate = new Date(new Date().setDate(new Date().getDate() + 1));
    nextDayDate.setHours(0, 0, 0, 0);
    let preferredDates: Date[] = [];
    if (
        slotHoldDates &&
        (slotHoldDates.includes(String(currentDate)) ||
            slotHoldDates.includes(String(nextDayDate)))
    ) {
        if (!slotHoldDates.includes(String(currentDate))) {
            preferredDates.push(currentDate);
        }
        let nextDay = 1;
        const currentPreferredDateLength = preferredDates.length;
        while (preferredDates.length === currentPreferredDateLength) {
            const nextDate = new Date(
                new Date().setDate(new Date().getDate() + nextDay)
            );
            nextDate.setHours(0, 0, 0, 0);
            if (slotHoldDates.includes(String(nextDate))) {
                nextDay += 1;
            } else {
                preferredDates.push(nextDate);
            }
        }
    } else {
        preferredDates = [currentDate, nextDayDate];
    }
    return preferredDates;
};

export const getExtraDatesToShowSlots = (slotOptions): Date[] => {
    let extraDatesToShowSlotArray = [];
    slotOptions.forEach((option) => {
        const { extra_dates_to_show_slot: extraDatesToShowSlots } = option;
        if (extraDatesToShowSlots) {
            extraDatesToShowSlotArray = [
                ...extraDatesToShowSlotArray,
                ...formatDatesStringToNewDateFormat(extraDatesToShowSlots)
            ];
        }
    });
    const extraDatesToShowSlotsWithIn1Month = [];
    extraDatesToShowSlotArray.forEach((eachDate) => {
        const dateToCheck = new Date(eachDate);
        const currentDate = new Date();
        currentDate.setHours(0, 0, 0, 0);
        const oneMonthFromNow = addWeeks(currentDate, 4);
        const isWithin1Month = isWithinInterval(dateToCheck, {
            start: currentDate,
            end: oneMonthFromNow
        });
        if (isWithin1Month) extraDatesToShowSlotsWithIn1Month.push(dateToCheck);
    });

    const uniqueDates = [];
    const seenDateStrings = [];

    extraDatesToShowSlotsWithIn1Month.forEach((date) => {
        const dateString = String(date);
        if (!seenDateStrings.includes(dateString)) {
            seenDateStrings.push(dateString);
            uniqueDates.push(date);
        }
    });

    return uniqueDates;
};

export const getSlotsBasedOnShouldShowNextWeek = (
    slotOptions,
    optionType: string,
    date: Date,
    isDateBasedSlots?: boolean
) => {
    return slotOptions.filter((option) => {
        const { should_show_next_week: shouldShowNextWeek } = option;
        if (isDateBasedSlots) return true;
        else if (!shouldShowNextWeek) return false;
        const nextDates = getNextDatesForDayCodes(option.preferred_days);
        const extraDatesToShowSlotsArray =
            formatDatesStringToNewDateFormat(nextDates);
        return (
            option.option_type === optionType &&
            extraDatesToShowSlotsArray.includes(String(date))
        );
    });
};

export const getExtraDatesToShowSlotsOnNextWeek = (
    slotOptions
): Date[] => {
    let datesToShowSlotsOnNextWeek = [];
    slotOptions.forEach((option) => {
        const { should_show_next_week: shouldShowNextWeek } = option;
        if (shouldShowNextWeek) {
            const nextDates = getNextDatesForDayCodes(option.preferred_days);
            datesToShowSlotsOnNextWeek = [
                ...datesToShowSlotsOnNextWeek,
                ...formatDatesStringToNewDateFormat(nextDates)
            ];
        }
    });

    return Array.from(new Set(datesToShowSlotsOnNextWeek))
        .map((date) => new Date(date))
        .sort(compareAsc);
};

export const getImmediateSlot = (): string => {
    const currentTime = new Date();

    const endTime = addHours(currentTime, 1);

    const formatTime = (time: Date) => {
        return format(time, 'hh:mma');
    };

    return `${formatTime(currentTime)} - ${formatTime(endTime)}`;
};

export const getTimeSlotsBasedOnFormattedOptions = (formattedOptions) => {
    formattedOptions.forEach((option) => {
        const { label } = option;
        try {
            const timeSlotOptions = [];
            option.options.forEach((timeSlot) => {
                const { time_value: timeValue } = timeSlot;
                timeSlotOptions.push({
                    value: `${label}${STRING_DELIMITER}${timeValue}`,
                    label: timeValue
                });
            });
            option.formattedOptions = timeSlotOptions;
        } catch (err) {
            console.error(err);
        }
    });

    return formattedOptions;
};

